/**
 * useTeam Hook
 *
 * Real-time listener for team members from Firestore 'users' collection
 * Returns all active team members with their roles
 *
 * MULTI-TENANCY: Filters by tenantId to ensure data isolation
 */

import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { User } from '../utils/seedData';
import { useTenantId, tenantIdReadUnion } from './useTenantId';

// Wave 2: TeamMember moved to src/types/team.types.ts so services can
// type against it without a hooks-layer import. Re-exported here for the
// existing hook/component importers.
export type { TeamMember } from '@/types/team.types';
import type { TeamMember } from '@/types/team.types';


// Map Firestore User role to TeamMember role
const mapUserRoleToTeamRole = (userRole: User['role']): TeamMember['role'] => {
  switch (userRole) {
    case 'Admin':
      return 'Admin';
    case 'Tech':
    case 'GuestTech':
      // Guests are technicians at the team level; isGuest flag is set
      // separately so the scheduler still picks them up while consult
      // pickers can filter them out.
      return 'Technician';
    case 'Sales':
      return 'Sales';
    case 'Ops':
    case 'Provisioning':
      return 'Office';
    default:
      return 'Office';
  }
};

export function useTeam() {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const tenantId = useTenantId();

  useEffect(() => {
    // Tenant isolation — users is per-tenant. Single-field `in` filter (no
    // orderBy) needs no composite index; we sort by name client-side below.
    // tenantIdReadUnion is a no-op today (legacy alias dead) but kept as policy
    // insurance per CLAUDE.md.
    // ⚠️ Rollout: run `npm run backfill:users-schedule-tenant -- --apply` in
    // prod BEFORE deploying this filter, or any active user whose doc predates
    // the tenantId rollout drops out of the team list (scheduler / pickers).
    const q = query(
      collection(db, 'users'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId))
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // The user doc carries denormalized leave-status fields that aren't on
        // the canonical User interface; widen locally so they're typed reads.
        type UserWithLeaveStatus = User & {
          leaveStatus?: TeamMember['status'];
          statusSetAt?: string;
          statusSetBy?: string;
        };
        const usersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as UserWithLeaveStatus[];

        // Filter active users and map to TeamMember format. Sorted by name
        // client-side (the query drops orderBy to avoid a composite index).
        const teamData: TeamMember[] = usersData
          .filter((user) => user.status === 'Active')
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          .map((user) => ({
            id: user.id || '',
            name: user.name,
            role: mapUserRoleToTeamRole(user.role),
            email: user.email,
            phone: user.phone,
            active: true,
            dualAccess: user.dualAccess || false,
            isGuest: user.role === 'GuestTech',
            status: user.leaveStatus || null,
            statusSetAt: user.statusSetAt,
            statusSetBy: user.statusSetBy,
            workHours: user.workHours,
            tilBalance: typeof user.tilBalance === 'number' ? user.tilBalance : undefined,
          }));

        setTeam(teamData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching team:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tenantId]);

  return { team, loading, error };
}

/**
 * Get all technicians (including dualAccess users).
 * Used by the scheduler and other contexts where all techs need to appear.
 *
 * dualAccess covers office-first people (Reuben, Guy, Brady) whose primary
 * Firestore role may be Admin / Ops / Sales but who still go into the field
 * occasionally. They're included here so the scheduler's
 * `isHiddenUnlessScheduled` mechanism can manage their column visibility
 * (hide when no jobs, show when scheduled or force-deployed).
 * Field-only surfaces use `useFieldTechnicians` to exclude them.
 */
export function useTechnicians() {
  const { team, loading, error } = useTeam();
  const technicians = team.filter(
    (member) => member.role === 'Technician' || member.dualAccess
  );
  return { technicians, loading, error };
}

/**
 * Get field-only senior technicians (excludes dualAccess users like Reuben/Guy
 * who are office-first, AND excludes GuestTech / trainee accounts who shouldn't
 * receive fault consult requests or tech requests). Used by consult pickers,
 * tech-request lists, and similar "needs an experienced tech" surfaces.
 */
export function useFieldTechnicians() {
  const { team, loading, error } = useTeam();
  const technicians = team.filter(
    (member) => member.role === 'Technician' && !member.dualAccess && !member.isGuest
  );
  return { technicians, loading, error };
}
